import {FC, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useNavigate} from 'react-router-dom'
import {useAuth} from '../auth'
import {
  canOpenNewProforma,
  OPEN_NEW_PROFORMA_STATE,
  PROFORMA_INTRO_EVENT,
  SALES_INVOICES_PATH,
  shouldAnnounceProforma,
} from './proformaIntro'
import '../apps/invoice-management/invoice-print/intro/printPageIntro.scss'

type Feature = {icon: string; title: string; text: string}

const FEATURES: Feature[] = [
  {
    icon: 'la-file-signature',
    title: 'ساخت پیش‌فاکتور',
    text: 'در «صورتحساب‌های فروش»، از «صورتحساب جدید › پیش‌فاکتور جدید»: همان فرم صورتحساب، با تاریخ اعتبار و شرایط پرداخت و تحویل.',
  },
  {
    icon: 'la-calendar-check',
    title: 'تاریخ اعتبار',
    text: 'پیش‌فرض ۷ روز است و در «تنظیمات کسب‌وکار › پیش‌فاکتور» تغییر می‌کند. پیش‌فاکتور منقضی‌شده با «تمدید اعتبار» دوباره معتبر می‌شود.',
  },
  {
    icon: 'la-exchange-alt',
    title: 'تبدیل به صورتحساب',
    text: 'با «تبدیل به صورتحساب» یک صورتحساب اصلی ثبت می‌شود؛ تاریخ، شماره و حافظهٔ مالیاتی را همان‌جا انتخاب می‌کنید و صورتحساب مثل همیشه ارسال می‌شود.',
  },
  {
    icon: 'la-shield-alt',
    title: 'بدون اثر مالیاتی',
    text: 'به سامانهٔ مؤدیان ارسال نمی‌شود، شمارهٔ منحصربه‌فرد مالیاتی ندارد، شماره‌گذاری‌اش جداست و در جمع‌ها و گزارش‌ها نمی‌آید.',
  },
]

/**
 * The introduction of the proforma, opened only from its item in «امکانات تازه» (no window at
 * sign-in, user decision 2026-10-13). «ایجاد پیش‌فاکتور» opens the sales invoices list with the
 * proforma form.
 */
export const ProformaIntroGate: FC = () => {
  const {currentUser} = useAuth()
  const navigate = useNavigate()
  const [show, setShow] = useState(false)

  useEffect(() => {
    const open = () => setShow(true)
    window.addEventListener(PROFORMA_INTRO_EVENT, open)
    return () => window.removeEventListener(PROFORMA_INTRO_EVENT, open)
  }, [])

  if (!show || !shouldAnnounceProforma(currentUser)) return null

  const close = () => setShow(false)
  const create = () => {
    setShow(false)
    navigate(SALES_INVOICES_PATH, {state: OPEN_NEW_PROFORMA_STATE})
  }

  return (
    <Modal
      show
      onHide={close}
      centered
      size='lg'
      fullscreen='sm-down'
      scrollable
      dialogClassName='ppi-modal'
      aria-labelledby='proforma-intro-title'
    >
      <Modal.Body className='ppi-modal__body'>
        <button type='button' className='ppi-modal__close' onClick={close} aria-label='بستن'>
          <i className='las la-times' aria-hidden='true' />
        </button>

        <div className='ppi-content'>
          <span className='ppi-eyebrow'>
            <i className='las la-star' aria-hidden='true' />
            تازه
          </span>
          <h2 id='proforma-intro-title' className='ppi-title'>
            پیش‌فاکتور
          </h2>
          <p className='ppi-lead'>
            پیشنهاد قیمت را با همان اقلام و مبالغ صورتحساب، با تاریخ اعتبار برای خریدار بفرستید و پس
            از قطعی شدن، با یک کلیک به صورتحساب تبدیل کنید. پیش‌فاکتور در کنار صورتحساب‌های فروش با
            برچسب جداگانه نمایش داده می‌شود و فقط وقتی ساخته می‌شود که خودتان آن را انتخاب کنید.
          </p>

          <ul className='ppi-features' aria-label='امکانات پیش‌فاکتور'>
            {FEATURES.map((feature) => (
              <li key={feature.title} className='ppi-feature'>
                <span className='ppi-feature__icon' aria-hidden='true'>
                  <i className={`las ${feature.icon}`} />
                </span>
                <span>
                  <strong>{feature.title}</strong>
                  <span className='ppi-feature__text'>{feature.text}</span>
                </span>
              </li>
            ))}
          </ul>

          <section className='ppi-section' aria-labelledby='proforma-intro-more'>
            <h3 id='proforma-intro-more' className='ppi-section__title'>
              راه‌های دیگر و پیگیری
            </h3>
            <p className='ppi-note'>
              در بارگذاری اکسل، گزینهٔ «ثبت ردیف‌های این فایل به‌عنوان پیش‌فاکتور» را پیش از انتخاب
              فایل روشن کنید؛ الگوی فایل همان الگوی صورتحساب است. در API نسخه ۲ هم فیلد{' '}
              <bdi dir='ltr'>proforma: true</bdi> همین کار را می‌کند. در فهرست، فیلترهای «نوع سند» و
              «وضعیت اعتبار» پیش‌فاکتورهای معتبر، رو به انقضا، منقضی‌شده یا تبدیل‌شده را جدا می‌کنند
              و چاپ آن با عنوان «پیش‌فاکتور» است.
            </p>
          </section>
        </div>
      </Modal.Body>
      <Modal.Footer className='ppi-modal__footer'>
        <button type='button' className='btn btn-light' onClick={close}>
          بستن
        </button>
        {canOpenNewProforma(currentUser) && (
          <button type='button' className='btn btn-primary' onClick={create}>
            <i className='las la-file-signature' aria-hidden='true' />
            ایجاد پیش‌فاکتور
          </button>
        )}
      </Modal.Footer>
    </Modal>
  )
}
